import React, { useEffect, useState } from 'react';
import { studiesData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicContent, fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

const EBC_ICON = 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/jkk586h5_icon2.png';

const Studies = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  // The pyramid layout expects exactly 5 items. When the CMS has 5 or more
  // published services, the first 5 replace the static fallback. Otherwise the
  // page keeps the original static content — this preserves the design
  // constraint you approved earlier.
  const [studies, setStudies] = useState(studiesData);
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicContent('services').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;

      const mapped = items.map((it, idx) => ({
        id: it.id || idx + 1,
        title: it.title_ar || it.title_en || '',
        titleEn: it.title_en || it.title_ar || '',
        description: it.description_ar || '',
        descriptionEn: it.description_en || it.description_ar || '',
        shellImage:
          it.image ||
          studiesData[idx]?.shellImage ||
          EBC_ICON,
      }));

      setStudies(mapped);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);
  return (
    <section id="studies" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-50/50 via-blue-50/30 to-teal-50/50 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-300/10 rounded-full blur-3xl animate-float-slow animation-delay-2000" />
      </div>

      <div className="relative container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-3 mb-4">
            <h2 data-testid="studies-heading" className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">
              {sec('studies_heading', isAr ? 'الدراسات' : 'Our Studies')}
            </h2>
          </div>
          <p data-testid="studies-subtitle" className="text-lg text-slate-600 dark:text-slate-400">
            {sec('studies_subtitle', isAr ? 'مرر الماوس على البطاقة لاستكشاف خدماتنا' : 'Hover over a card to explore our services')}
          </p>
        </div>

        {/* Flexible Studies Layout — max 3 per row, last row centered */}
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-8">
            {studies.map((study, index) => (
              <div
                key={study.id}
                className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
              >
                <StudyCard
                  study={study}
                  index={index}
                  isAr={isAr}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const StudyCard = ({ study, index, isAr = true }) => {
  const title = isAr ? study.title : (study.titleEn || study.title);
  const desc  = isAr ? study.description : (study.descriptionEn || study.description);
  return (
  <div
    className="relative group animate-fade-in-up"
    style={{ animationDelay: `${index * 0.1}s` }}
    data-testid={`study-card-${study.id}`}
  >
    <div className="relative transition-all duration-500 hover:scale-105">
      <div className="relative w-full aspect-square">
        {/* Topic image */}
        <img
          src={study.shellImage}
          alt={title}
          className="w-full h-full object-cover rounded-3xl shadow-2xl
                   opacity-95 group-hover:opacity-25 transition-all duration-500"
        />

        {/* Subtle gradient darkening at bottom for legibility */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-slate-900/45 via-transparent to-transparent
                      group-hover:opacity-0 transition-opacity duration-500 pointer-events-none" />

        {/* Title overlay (visible by default) */}
        <div className="absolute inset-0 flex items-center justify-center
                      group-hover:opacity-0 transition-all duration-500">
          <div className="text-center p-6 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md rounded-2xl shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h3>
          </div>
        </div>

        {/* Description overlay (visible on hover) */}
        <div className="absolute inset-0 rounded-3xl bg-white/95 dark:bg-slate-800/95
                      backdrop-blur-xl border-2 border-cyan-400 dark:border-cyan-600
                      opacity-0 invisible group-hover:opacity-100 group-hover:visible
                      transition-all duration-500 overflow-hidden">
          <div className="absolute inset-0 p-6 flex flex-col justify-center">
            <div className={isAr ? 'text-right' : 'text-left'}>
          <p
            className="text-base leading-relaxed text-slate-700 dark:text-slate-300"
            style={{ whiteSpace: 'pre-line' }}
          >
                {desc}
              </p>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cyan-500/20 to-transparent pointer-events-none" />
        </div>
      </div>
    </div>

    {/* Glow effect on hover */}
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-cyan-500/30 to-blue-500/30 blur-xl -z-10
                  opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
  </div>
  );
};

export default Studies;
